import { ReactElement } from 'react'
import {
  Box,
  Card,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Bolt, Schedule } from '@mui/icons-material'
import type { AssignmentMode } from './unified-assignment-types'

type AssignmentModeSelectorProps = {
  mode: AssignmentMode
  onModeChange: (mode: AssignmentMode) => void
  disabled?: boolean
}

export default function AssignmentModeSelector({
  mode,
  onModeChange,
  disabled = false,
}: AssignmentModeSelectorProps): ReactElement {
  return (
    <Card className="p-6 mb-6">
      <Typography variant="h6" component="h2" className="mb-3">
        When should this assignment go live?
      </Typography>
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={(_, newMode) => {
          if (newMode !== null) {
            onModeChange(newMode as AssignmentMode)
          }
        }}
        fullWidth
        disabled={disabled}
        orientation="horizontal"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          '& .MuiToggleButton-root': {
            py: 2,
            px: 3,
            textTransform: 'none',
            flexDirection: 'column',
            alignItems: 'flex-start',
            border: '2px solid',
            borderColor: 'divider',
            flex: 1,
            '&.Mui-selected': {
              borderColor: 'primary.main',
              bgcolor: 'rgba(33, 150, 243, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(33, 150, 243, 0.12)',
              },
            },
            '&:not(.Mui-selected)': {
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
          },
        }}
      >
        <ToggleButton value="immediate">
          <Box className="flex items-center gap-2 mb-1">
            <Bolt sx={{ color: 'primary.main' }} />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
              }}
            >
              Immediately
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Assignment is created right away
          </Typography>
        </ToggleButton>

        <ToggleButton value="scheduled">
          <Box className="flex items-center gap-2 mb-1">
            <Schedule sx={{ color: 'primary.main' }} />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
              }}
            >
              On a Future Date
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Automatically created on schedule (bypasses threshold notifications)
          </Typography>
        </ToggleButton>
      </ToggleButtonGroup>
    </Card>
  )
}
